/**
 * MMO Registration Scene - Optimized account creation with immersive visuals
 * Features: Parallax stars, ships, responsive design
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { EventBus } from '../../utils/EventBus';
import { I18n } from '../../i18n/I18nService';
import { getHUDSceneKey } from '../../utils/PlatformRouter';

const API_URL = '/api';
const CONNECTION_WATCHDOG_MS = 45000;
const REFERRAL_STORAGE_KEY = 'crimson_mandate_ref';
const REFERRAL_CODE_PATTERN = /^[A-Za-z0-9_-]{4,24}$/;

interface RegistrationPayload {
  username: string;
  email: string;
  password: string;
  tosAccepted: true;
  refCode?: string;
}

function normalizeReferralCode(code: string | null | undefined): string | null {
  const normalized = (code || '').trim().toUpperCase();
  return REFERRAL_CODE_PATTERN.test(normalized) ? normalized : null;
}

function getReferralCode(): string | null {
  let fromUrl: string | null = null;
  try {
    fromUrl = normalizeReferralCode(new URLSearchParams(window.location.search).get('ref'));
  } catch (_err) {
    fromUrl = null;
  }

  if (fromUrl) {
    try {
      localStorage.setItem(REFERRAL_STORAGE_KEY, fromUrl);
    } catch (_err) {}
    return fromUrl;
  }

  try {
    return normalizeReferralCode(localStorage.getItem(REFERRAL_STORAGE_KEY));
  } catch (_err) {
    return null;
  }
}

// Styling constants (same as login)
const LOGO_Y_PERCENT = 0.15;
const FORM_TOP_PERCENT = 30;
const FORM_WIDTH = 440;

// CSS animations (cleaned up - removed unused)
const STYLE_ID = 'mmo-register-styles';
const GLOBAL_STYLES = `
  @keyframes formSlideIn {
    from { opacity: 0; transform: translateX(-50%) translateY(20px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }

  @keyframes buttonGlowPulse {
    0%, 100% { box-shadow: 0 0 15px rgba(0, 255, 255, 0.15); }
    50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.25); }
  }

  @keyframes connectingPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes focusPulse {
    0% { box-shadow: 0 0 0 rgba(0, 255, 255, 0); }
    50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.5), inset 0 0 10px rgba(0, 255, 255, 0.1); }
    100% { box-shadow: 0 0 15px rgba(0, 255, 255, 0.3), inset 0 0 10px rgba(0, 255, 255, 0.05); }
  }

  @keyframes logoGlowPulse {
    0%, 100% { text-shadow: 0 0 8px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.4), 0 0 35px rgba(0, 102, 255, 0.25); }
    50% { text-shadow: 0 0 12px rgba(0, 255, 255, 0.85), 0 0 28px rgba(0, 255, 255, 0.5), 0 0 45px rgba(0, 102, 255, 0.35); }
  }

  .stellar-logo-text {
    font-family: 'Orbitron', sans-serif;
    font-weight: 900;
    font-size: clamp(26px, 5vw, 54px);
    letter-spacing: 5px;
    text-transform: uppercase;
    color: #00ffff;
    text-shadow: 0 0 8px rgba(0, 255, 255, 0.7), 0 0 20px rgba(0, 255, 255, 0.4), 0 0 35px rgba(0, 102, 255, 0.25);
    animation: logoGlowPulse 3s ease-in-out infinite;
    line-height: 1.1;
    text-align: center;
    margin: 0;
    user-select: none;
    pointer-events: none;
  }

  .stellar-logo-container {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .logo-tagline {
    font-family: 'Share Tech Mono', monospace;
    font-size: 16px;
    color: #a0c0d8;
    letter-spacing: 3px;
    margin-top: 12px;
    text-align: center;
  }

  .logo-tagline .separator {
    color: rgba(0, 255, 255, 0.5);
    margin: 0 8px;
  }

  .logo-tagline .earn {
    color: #00ff88;
  }

  .mmo-input:focus { animation: focusPulse 0.3s ease-out forwards; }
  .mmo-input, .mmo-button, .mmo-link { outline: none; }
  .mmo-input:focus-visible, .mmo-button:focus-visible, .mmo-link:focus-visible {
    outline: 2px solid #00ffff; outline-offset: 2px;
  }

  .register-form::-webkit-scrollbar { width: 8px; }
  .register-form::-webkit-scrollbar-track { background: rgba(0, 20, 40, 0.5); border: 1px solid rgba(0, 255, 255, 0.1); }
  .register-form::-webkit-scrollbar-thumb { background: rgba(0, 255, 255, 0.3); border-radius: 0; }
  .register-form::-webkit-scrollbar-thumb:hover { background: rgba(0, 255, 255, 0.5); }
  .register-form { scrollbar-width: thin; scrollbar-color: rgba(0, 255, 255, 0.3) rgba(0, 20, 40, 0.5); }

  /* Mobile responsive adjustments */
  @media (max-width: 768px) {
    .register-form {
      top: 22% !important;
      width: 90% !important;
      max-width: 400px !important;
      padding: 20px 15px !important;
      max-height: calc(100vh - 25% - 20px) !important;
    }
    .stellar-logo-text {
      font-size: clamp(24px, 8vw, 48px) !important;
      letter-spacing: 3px !important;
    }
  }

  @media (max-height: 700px) {
    .register-form {
      top: 18% !important;
    }
  }
`;

export class MMORegisterScene extends Phaser.Scene {
  // HTML elements
  private usernameInput!: HTMLInputElement;
  private emailInput!: HTMLInputElement;
  private passwordInput!: HTMLInputElement;
  private confirmPasswordInput!: HTMLInputElement;
  private registerButton!: HTMLButtonElement;
  private inputContainer!: HTMLDivElement;
  private statusElement!: HTMLDivElement;
  private strengthMeter!: HTMLDivElement;
  private strengthBar!: HTMLDivElement;
  private strengthText!: HTMLDivElement;
  private matchIndicator!: HTMLDivElement;
  private tosCheckbox!: HTMLInputElement;

  // Parallax
  private starLayers: Phaser.GameObjects.Graphics[] = [];
  private starData: { x: number; y: number; size: number; alpha: number }[][] = [];
  private mouseX: number = 0;
  private mouseY: number = 0;

  private isRegistering: boolean = false;
  private passwordVisible: boolean = false;
  private confirmPasswordVisible: boolean = false;
  private originalButtonText: string = I18n.t('register.create_commander');

  constructor() {
    super({ key: 'MMORegisterScene' });
  }

  // Parallax optimization
  private lastMouseX: number = 0;
  private lastMouseY: number = 0;
  private parallaxDirty: boolean = true;

  init(): void {
    this.starLayers = [];
    this.starData = [];
    this.parallaxDirty = true;
    this.lastMouseX = 0;
    this.lastMouseY = 0;
  }

  create(): void {
    const { width, height } = this.cameras.main;

    this.injectGlobalStyles();

    // Background
    if (this.textures.exists('bg-starfield')) {
      const bg = this.add.image(width / 2, height / 2, 'bg-starfield');
      bg.setDisplaySize(width, height);
      bg.setDepth(-10);
    }

    // Parallax stars
    this.createParallaxStars(width, height);

    // Planet removed per user request

    // Ships
    this.createShipSpawner(width, height);

    // Shooting stars
    this.createShootingStars(width, height);

    // Logo
    this.createLogo(width, height);

    // HUD data removed per user request

    // Form
    this.createRegistrationForm(width, height);

    // Version
    this.add.text(width / 2, height - 30, I18n.t('login.version'), {
      fontFamily: 'Share Tech Mono, monospace',
      fontSize: '12px',
      color: '#4a6a8a',
    }).setOrigin(0.5).setAlpha(0.6);

    // Mouse tracking for parallax (only mark dirty when mouse actually moves)
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      const newX = (pointer.x - width / 2) / width;
      const newY = (pointer.y - height / 2) / height;
      if (Math.abs(newX - this.lastMouseX) > 0.001 || Math.abs(newY - this.lastMouseY) > 0.001) {
        this.mouseX = newX;
        this.mouseY = newY;
        this.lastMouseX = newX;
        this.lastMouseY = newY;
        this.parallaxDirty = true;
      }
    });

    // Update loop for parallax (optimized - only redraws when needed)
    this.events.on('update', this.updateParallax, this);
    this.events.on('shutdown', this.shutdown, this);
    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  private injectGlobalStyles(): void {
    if (!document.getElementById(STYLE_ID)) {
      const style = document.createElement('style');
      style.id = STYLE_ID;
      style.textContent = GLOBAL_STYLES;
      document.head.appendChild(style);
    }
  }

  private createParallaxStars(width: number, height: number): void {
    const layerConfigs = [
      { count: 50, speedFactor: 0.02, sizeRange: [0.5, 1.5], alpha: 0.25 },
      { count: 30, speedFactor: 0.05, sizeRange: [1, 2], alpha: 0.4 },
      { count: 18, speedFactor: 0.1, sizeRange: [1.5, 3], alpha: 0.7 },
    ];

    layerConfigs.forEach((config, index) => {
      const graphics = this.add.graphics();
      graphics.setDepth(-5 + index);
      this.starLayers.push(graphics);

      const stars: { x: number; y: number; size: number; alpha: number }[] = [];
      for (let i = 0; i < config.count; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          size: config.sizeRange[0] + Math.random() * (config.sizeRange[1] - config.sizeRange[0]),
          alpha: config.alpha * (0.5 + Math.random() * 0.5),
        });
      }
      this.starData.push(stars);
    });
  }

  private updateParallax(): void {
    // Skip redraw if nothing changed (major performance optimization)
    if (!this.parallaxDirty) return;
    this.parallaxDirty = false;

    const { width, height } = this.cameras.main;
    const speedFactors = [0.02, 0.05, 0.1];

    this.starLayers.forEach((graphics, layerIndex) => {
      graphics.clear();
      const stars = this.starData[layerIndex];
      const factor = speedFactors[layerIndex];

      stars.forEach((star) => {
        const offsetX = this.mouseX * factor * 100;
        const offsetY = this.mouseY * factor * 100;
        let drawX = star.x + offsetX;
        let drawY = star.y + offsetY;

        if (drawX < 0) drawX += width;
        if (drawX > width) drawX -= width;
        if (drawY < 0) drawY += height;
        if (drawY > height) drawY -= height;

        graphics.fillStyle(0xffffff, star.alpha);
        graphics.fillCircle(drawX, drawY, star.size);
      });
    });
  }

  private activeShips: number = 0;
  private readonly MAX_SHIPS = 3;

  private createShipSpawner(width: number, height: number): void {
    // Spawn ships periodically, checking max limit
    this.time.addEvent({
      delay: 4000,
      callback: () => {
        if (this.activeShips < this.MAX_SHIPS) {
          this.spawnShip(width, height);
        }
      },
      loop: true,
    });
    // Initial ships with staggered timing
    this.time.delayedCall(1000, () => this.spawnShip(width, height));
    this.time.delayedCall(3000, () => {
      if (this.activeShips < this.MAX_SHIPS) this.spawnShip(width, height);
    });
  }

  private spawnShip(width: number, height: number): void {
    if (this.activeShips >= this.MAX_SHIPS) return;
    this.activeShips++;

    const ship = this.add.graphics();
    ship.setDepth(-2);

    // Random direction: left-to-right or right-to-left
    const goingRight = Math.random() > 0.5;
    const startX = goingRight ? -30 : width + 30;
    const endX = goingRight ? width + 30 : -30;
    const startY = Phaser.Math.Between(50, height * 0.4);
    const endY = startY + Phaser.Math.Between(-50, 100);

    // Random ship type (0-3)
    const shipType = Phaser.Math.Between(0, 3);

    const drawShip = (x: number, y: number, alpha: number) => {
      ship.clear();
      ship.fillStyle(0x3a5a7a, alpha);
      // Direction multiplier: 1 for right-facing, -1 for left-facing
      const d = goingRight ? 1 : -1;

      switch (shipType) {
        case 0: // Fighter - small, fast
          ship.fillTriangle(x, y, x - 12 * d, y + 4, x - 12 * d, y - 4);
          ship.fillTriangle(x - 8 * d, y - 3, x - 14 * d, y - 8, x - 14 * d, y - 3);
          ship.fillTriangle(x - 8 * d, y + 3, x - 14 * d, y + 8, x - 14 * d, y + 3);
          ship.fillStyle(0x00aaff, alpha * 0.8);
          ship.fillCircle(x - 13 * d, y, 2);
          break;

        case 1: // Cruiser - larger, bulkier
          ship.fillRect(goingRight ? x - 20 : x, y - 3, 20, 6);
          ship.fillTriangle(x, y, x - 5 * d, y - 5, x - 5 * d, y + 5);
          ship.fillRect(goingRight ? x - 18 : x + 10, y - 6, 8, 3);
          ship.fillRect(goingRight ? x - 18 : x + 10, y + 3, 8, 3);
          ship.fillStyle(0x00aaff, alpha * 0.8);
          ship.fillCircle(x - 21 * d, y - 1, 2);
          ship.fillCircle(x - 21 * d, y + 1, 2);
          break;

        case 2: // Freighter - big cargo ship
          ship.fillRect(goingRight ? x - 25 : x + 3, y - 5, 22, 10);
          ship.fillTriangle(x - 3 * d, y, x - 8 * d, y - 6, x - 8 * d, y + 6);
          ship.fillRect(goingRight ? x - 22 : x + 16, y - 8, 6, 3);
          ship.fillRect(goingRight ? x - 22 : x + 16, y + 5, 6, 3);
          ship.fillStyle(0xffaa00, alpha * 0.6);
          ship.fillCircle(x - 26 * d, y - 2, 2);
          ship.fillCircle(x - 26 * d, y + 2, 2);
          break;

        case 3: // Interceptor - sleek, angular
          ship.fillTriangle(x, y, x - 18 * d, y + 2, x - 18 * d, y - 2);
          ship.fillTriangle(x - 10 * d, y - 2, x - 20 * d, y - 7, x - 20 * d, y - 2);
          ship.fillTriangle(x - 10 * d, y + 2, x - 20 * d, y + 7, x - 20 * d, y + 2);
          ship.fillStyle(0xff4466, alpha * 0.8);
          ship.fillCircle(x - 18 * d, y, 1.5);
          break;
      }
    };

    this.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: Phaser.Math.Between(8000, 14000),
      ease: 'Linear',
      onUpdate: (tween) => {
        const p = tween.getValue() ?? 0;
        const x = startX + (endX - startX) * p;
        const y = startY + (endY - startY) * p;
        drawShip(x, y, 0.6);
      },
      onComplete: () => {
        ship.destroy();
        this.activeShips--;
      },
    });
  }

  private logoContainer: HTMLDivElement | null = null;

  private createLogo(_width: number, height: number): void {
    const logoY = height * LOGO_Y_PERCENT;

    // Create HTML-based glowing text logo
    this.logoContainer = document.createElement('div');
    this.logoContainer.className = 'stellar-logo-container';
    this.logoContainer.style.top = `${logoY - 40}px`;

    const stellarText = document.createElement('h1');
    stellarText.className = 'stellar-logo-text';
    stellarText.textContent = I18n.t('login.brand_crimson');

    const conquestText = document.createElement('h1');
    conquestText.className = 'stellar-logo-text';
    conquestText.style.marginTop = '-10px';
    conquestText.textContent = I18n.t('login.brand_mandate');

    // Tagline under logo
    const tagline = document.createElement('div');
    tagline.className = 'logo-tagline';
    tagline.innerHTML = I18n.t('login.tagline');

    this.logoContainer.appendChild(stellarText);
    this.logoContainer.appendChild(conquestText);
    this.logoContainer.appendChild(tagline);
    document.body.appendChild(this.logoContainer);

    // Clean up on scene shutdown
    this.events.once('shutdown', () => {
      if (this.logoContainer) {
        this.logoContainer.remove();
        this.logoContainer = null;
      }
    });
  }

  private createShootingStars(width: number, height: number): void {
    this.time.addEvent({ delay: 4000, callback: () => this.spawnShootingStar(width, height), loop: true });
    this.time.delayedCall(2000, () => this.spawnShootingStar(width, height));
  }

  private spawnShootingStar(width: number, height: number): void {
    const star = this.add.graphics();
    star.setDepth(1);

    const fromRight = Math.random() > 0.5;
    const startX = fromRight ? width + 50 : Phaser.Math.Between(100, width);
    const startY = fromRight ? Phaser.Math.Between(0, height * 0.5) : -50;
    const endX = startX - Phaser.Math.Between(300, 600);
    const endY = startY + Phaser.Math.Between(200, 400);
    const length = Phaser.Math.Between(40, 100);
    const alpha = Phaser.Math.FloatBetween(0.3, 0.6);

    this.tweens.add({
      targets: { progress: 0 },
      progress: 1,
      duration: Phaser.Math.Between(1500, 2500),
      ease: 'Cubic.easeIn',
      onUpdate: (tween) => {
        const p = tween.getValue() ?? 0;
        const currentX = startX + (endX - startX) * p;
        const currentY = startY + (endY - startY) * p;
        star.clear();

        const angle = Math.atan2(endY - startY, endX - startX);
        const tailX = currentX - Math.cos(angle) * length;
        const tailY = currentY - Math.sin(angle) * length;

        for (let i = 0; i < 5; i++) {
          const t = i / 5;
          star.lineStyle(2 - i * 0.3, 0xffffff, alpha * (1 - t * 0.7));
          star.moveTo(tailX + (currentX - tailX) * t, tailY + (currentY - tailY) * t);
          star.lineTo(tailX + (currentX - tailX) * (t + 0.25), tailY + (currentY - tailY) * (t + 0.25));
          star.strokePath();
        }
        star.fillStyle(0xffffff, alpha);
        star.fillCircle(currentX, currentY, 2);
      },
      onComplete: () => star.destroy(),
    });
  }

  private createRegistrationForm(_width: number, _height: number): void {
    this.inputContainer = document.createElement('div');
    this.inputContainer.className = 'register-form';
    this.inputContainer.style.cssText = `
      position: absolute; left: 50%; top: ${FORM_TOP_PERCENT}%; transform: translateX(-50%);
      width: ${FORM_WIDTH}px; max-width: calc(100vw - 40px);
      max-height: calc(100vh - ${FORM_TOP_PERCENT}% - 20px); overflow-y: auto;
      z-index: 1000; font-family: 'Share Tech Mono', monospace; padding: 30px 40px;
      background: rgba(3, 10, 22, 0.55); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(0, 255, 255, 0.2);
      box-shadow: inset 0 0 30px rgba(0, 255, 255, 0.03), 0 0 20px rgba(0, 0, 0, 0.5);
      opacity: 0; animation: formSlideIn 0.4s ease-out 0.2s forwards;
    `;

    // Username (no header - logo is the hero)
    this.inputContainer.appendChild(this.createLabel(I18n.t('register.commander_name')));
    this.usernameInput = this.createInput('text', I18n.t('register.username_placeholder'), 1);
    this.usernameInput.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.emailInput.focus(); });
    this.inputContainer.appendChild(this.usernameInput);

    // Email
    this.inputContainer.appendChild(this.createLabel(I18n.t('register.email_address')));
    this.emailInput = this.createInput('email', I18n.t('login.email_placeholder'), 2);
    this.emailInput.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.passwordInput.focus(); });
    this.inputContainer.appendChild(this.emailInput);

    // Password
    this.inputContainer.appendChild(this.createLabel(I18n.t('register.password')));
    const passwordContainer = this.createPasswordField(false, 3);
    this.inputContainer.appendChild(passwordContainer);

    // Strength meter
    this.strengthMeter = document.createElement('div');
    this.strengthMeter.style.cssText = `margin-bottom: 14px;`;

    this.strengthBar = document.createElement('div');
    this.strengthBar.style.cssText = `width: 100%; height: 6px; background: rgba(0, 20, 40, 0.7); border: 1px solid rgba(0, 255, 255, 0.3); margin-bottom: 8px; overflow: hidden;`;

    const strengthFill = document.createElement('div');
    strengthFill.id = 'strength-fill';
    strengthFill.style.cssText = `width: 0%; height: 100%; background: #ff4444; transition: all 0.3s;`;
    this.strengthBar.appendChild(strengthFill);

    this.strengthText = document.createElement('div');
    this.strengthText.style.cssText = `font-size: 13px; color: #6a8a9a; display: flex; gap: 20px; flex-wrap: wrap;`;
    this.strengthText.innerHTML = `<span data-check="length">${I18n.t('register.strength_length')}</span><span data-check="number">${I18n.t('register.strength_number')}</span><span data-check="symbol">${I18n.t('register.strength_symbol')}</span>`;

    this.strengthMeter.appendChild(this.strengthBar);
    this.strengthMeter.appendChild(this.strengthText);
    this.inputContainer.appendChild(this.strengthMeter);

    // Confirm password
    this.inputContainer.appendChild(this.createLabel(I18n.t('register.confirm_password')));
    const confirmContainer = this.createPasswordField(true, 5);
    this.inputContainer.appendChild(confirmContainer);

    // Match indicator
    this.matchIndicator = document.createElement('div');
    this.matchIndicator.style.cssText = `font-size: 13px; margin-bottom: 14px; display: none; height: 18px;`;
    this.inputContainer.appendChild(this.matchIndicator);

    // Terms of Service checkbox
    const tosContainer = document.createElement('div');
    tosContainer.style.cssText = `display: flex; align-items: flex-start; gap: 8px; margin-bottom: 14px;`;

    this.tosCheckbox = document.createElement('input');
    this.tosCheckbox.type = 'checkbox';
    this.tosCheckbox.id = 'tos-agree';
    this.tosCheckbox.tabIndex = 6;
    this.tosCheckbox.style.cssText = `width: 18px; height: 18px; margin-top: 2px; accent-color: #00ffff; cursor: pointer; flex-shrink: 0;`;

    const tosLabel = document.createElement('label');
    tosLabel.htmlFor = 'tos-agree';
    tosLabel.style.cssText = `font-size: 13px; color: #7aa0c0; cursor: pointer; line-height: 1.4;`;
    tosLabel.innerHTML = 'I agree to the <a href="/terms.html" target="_blank" style="color: #00ffff; text-decoration: underline;">Terms of Service</a> and <a href="/tokenomics.html" target="_blank" style="color: #00ffff; text-decoration: underline;">Privacy Policy</a>';

    tosContainer.appendChild(this.tosCheckbox);
    tosContainer.appendChild(tosLabel);
    this.inputContainer.appendChild(tosContainer);

    // Button
    this.registerButton = document.createElement('button');
    this.registerButton.textContent = this.originalButtonText;
    this.registerButton.className = 'mmo-button';
    this.registerButton.tabIndex = 7;
    this.registerButton.style.cssText = `
      width: 100%; padding: 16px 20px; font-size: 17px;
      font-family: 'Orbitron', sans-serif; font-weight: 700;
      background: rgba(0, 50, 70, 0.9); color: #00ffff;
      border: 2px solid #00ffff; border-radius: 0; cursor: pointer;
      letter-spacing: 2px; transition: background 0.2s, transform 0.2s;
      animation: buttonGlowPulse 2s ease-in-out infinite; margin-bottom: 16px;
    `;
    this.registerButton.addEventListener('mouseenter', () => {
      if (!this.isRegistering) {
        this.registerButton.style.background = 'rgba(0, 255, 255, 0.2)';
        this.registerButton.style.transform = 'translateY(-2px)';
      }
    });
    this.registerButton.addEventListener('mouseleave', () => {
      if (!this.isRegistering) {
        this.registerButton.style.background = 'rgba(0, 50, 70, 0.9)';
        this.registerButton.style.transform = 'translateY(0)';
      }
    });
    this.registerButton.addEventListener('click', () => { if (!this.isRegistering) this.handleRegistration(); });
    this.inputContainer.appendChild(this.registerButton);

    // Status
    this.statusElement = document.createElement('div');
    this.statusElement.style.cssText = `text-align: center; font-size: 15px; color: #ff6666; margin-bottom: 16px; min-height: 22px; display: none;`;
    this.inputContainer.appendChild(this.statusElement);

    // Back link
    const backContainer = document.createElement('div');
    backContainer.style.cssText = `text-align: center; padding-top: 10px; border-top: 1px solid rgba(0, 255, 255, 0.1);`;

    const backLink = document.createElement('a');
    backLink.textContent = I18n.t('register.back_to_login');
    backLink.href = '#';
    backLink.className = 'mmo-link';
    backLink.tabIndex = 8;
    backLink.style.cssText = `color: #7aa0c0; font-size: 16px; text-decoration: none; letter-spacing: 2px; transition: color 0.2s; display: inline-block; padding: 12px 24px;`;
    backLink.addEventListener('mouseenter', () => backLink.style.color = '#00ffff');
    backLink.addEventListener('mouseleave', () => backLink.style.color = '#7aa0c0');
    backLink.addEventListener('click', (e) => {
      e.preventDefault();
      this.cameras.main.fadeOut(200, 0, 0, 0);
      this.time.delayedCall(200, () => { this.cleanupInputs(); this.scene.start('MMOLoginScene'); });
    });

    backContainer.appendChild(backLink);
    this.inputContainer.appendChild(backContainer);

    document.body.appendChild(this.inputContainer);
    this.time.delayedCall(600, () => this.usernameInput.focus());
  }

  private createLabel(text: string): HTMLDivElement {
    const label = document.createElement('div');
    label.textContent = text;
    label.style.cssText = `color: #9ac0dc; font-size: 14px; margin-bottom: 8px; letter-spacing: 2px;`;
    return label;
  }

  private createInput(type: string, placeholder: string, tabIdx: number): HTMLInputElement {
    const input = document.createElement('input');
    input.type = type;
    input.placeholder = placeholder;
    input.className = 'mmo-input';
    input.tabIndex = tabIdx;
    input.style.cssText = `
      width: 100%; padding: 14px 16px; font-size: 17px;
      font-family: 'Share Tech Mono', monospace;
      background: rgba(0, 20, 40, 0.7); color: #ffffff;
      border: 2px solid rgba(0, 255, 255, 0.5); border-radius: 0;
      margin-bottom: 14px; box-sizing: border-box; outline: none;
      transition: border-color 0.2s;
    `;
    input.addEventListener('focus', () => input.style.borderColor = '#00ffff');
    input.addEventListener('blur', () => input.style.borderColor = 'rgba(0, 255, 255, 0.5)');
    return input;
  }

  private createPasswordField(isConfirm: boolean, tabIdx: number): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `position: relative; margin-bottom: 6px;`;

    const input = document.createElement('input');
    input.type = 'password';
    input.placeholder = I18n.t('login.password_placeholder');
    input.className = 'mmo-input';
    input.tabIndex = tabIdx;
    input.style.cssText = `
      width: 100%; padding: 14px 48px 14px 16px; font-size: 17px;
      font-family: 'Share Tech Mono', monospace;
      background: rgba(0, 20, 40, 0.7); color: #ffffff;
      border: 2px solid rgba(0, 255, 255, 0.5); border-radius: 0;
      box-sizing: border-box; outline: none; transition: border-color 0.2s;
    `;
    input.addEventListener('focus', () => input.style.borderColor = '#00ffff');
    input.addEventListener('blur', () => input.style.borderColor = 'rgba(0, 255, 255, 0.5)');

    if (isConfirm) {
      this.confirmPasswordInput = input;
      input.addEventListener('input', () => this.checkPasswordMatch());
      input.addEventListener('keypress', (e) => { if (e.key === 'Enter' && !this.isRegistering) this.handleRegistration(); });
    } else {
      this.passwordInput = input;
      input.addEventListener('input', () => this.updatePasswordStrength());
      input.addEventListener('keypress', (e) => { if (e.key === 'Enter') this.confirmPasswordInput.focus(); });
    }

    const toggleBtn = document.createElement('button');
    toggleBtn.type = 'button';
    toggleBtn.innerHTML = '<span style="position:relative;">👁</span>';
    toggleBtn.className = 'mmo-button';
    toggleBtn.tabIndex = tabIdx + 1;
    toggleBtn.style.cssText = `
      position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
      background: none; border: none; color: #6a9aba; font-size: 18px;
      cursor: pointer; padding: 8px; transition: color 0.2s;
    `;

    const updateToggleIcon = (visible: boolean) => {
      toggleBtn.innerHTML = visible
        ? '<span style="position:relative;display:inline-block;">👁<span style="position:absolute;left:50%;top:45%;width:120%;height:2px;background:#6a9aba;transform:translate(-50%,-50%) rotate(-45deg);"></span></span>'
        : '<span style="position:relative;">👁</span>';
    };

    toggleBtn.addEventListener('click', () => {
      if (isConfirm) {
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
        input.type = this.confirmPasswordVisible ? 'text' : 'password';
        updateToggleIcon(this.confirmPasswordVisible);
        toggleBtn.style.color = this.confirmPasswordVisible ? '#00ffff' : '#6a9aba';
      } else {
        this.passwordVisible = !this.passwordVisible;
        input.type = this.passwordVisible ? 'text' : 'password';
        updateToggleIcon(this.passwordVisible);
        toggleBtn.style.color = this.passwordVisible ? '#00ffff' : '#6a9aba';
      }
    });
    toggleBtn.addEventListener('mouseenter', () => toggleBtn.style.color = '#00ffff');
    toggleBtn.addEventListener('mouseleave', () => {
      const isVisible = isConfirm ? this.confirmPasswordVisible : this.passwordVisible;
      toggleBtn.style.color = isVisible ? '#00ffff' : '#6a9aba';
    });

    container.appendChild(input);
    container.appendChild(toggleBtn);
    return container;
  }

  private updatePasswordStrength(): void {
    const password = this.passwordInput.value;
    const checks = {
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const passedCount = Object.values(checks).filter(Boolean).length;
    const strengthFill = this.strengthBar.querySelector('#strength-fill') as HTMLDivElement;

    strengthFill.style.width = `${(passedCount / 3) * 100}%`;

    if (passedCount === 1) strengthFill.style.background = '#ff4444';
    else if (passedCount === 2) strengthFill.style.background = '#ffaa00';
    else if (passedCount === 3) strengthFill.style.background = '#00ffff';

    Object.entries(checks).forEach(([key, passed]) => {
      const span = this.strengthText.querySelector(`[data-check="${key}"]`) as HTMLSpanElement;
      if (span) {
        const text = span.textContent?.substring(2) || '';
        span.textContent = (passed ? '✓ ' : '⊘ ') + text;
        span.style.color = passed ? '#00ff88' : '#6a8a9a';
      }
    });

    if (this.confirmPasswordInput.value) this.checkPasswordMatch();
  }

  private checkPasswordMatch(): void {
    const password = this.passwordInput.value;
    const confirm = this.confirmPasswordInput.value;

    if (!confirm) { this.matchIndicator.style.display = 'none'; return; }

    this.matchIndicator.style.display = 'block';
    if (password === confirm) {
      this.matchIndicator.textContent = I18n.t('register.passwords_match');
      this.matchIndicator.style.color = '#00ff88';
    } else {
      this.matchIndicator.textContent = I18n.t('register.passwords_no_match');
      this.matchIndicator.style.color = '#ff6666';
    }
  }

  private setButtonLoading(loading: boolean): void {
    if (loading) {
      this.registerButton.textContent = I18n.t('register.creating');
      this.registerButton.style.animation = 'connectingPulse 1s ease-in-out infinite';
      this.registerButton.style.cursor = 'wait';
      this.registerButton.style.background = 'rgba(0, 80, 100, 0.9)';
    } else {
      this.registerButton.textContent = this.originalButtonText;
      this.registerButton.style.animation = 'buttonGlowPulse 2s ease-in-out infinite';
      this.registerButton.style.cursor = 'pointer';
      this.registerButton.style.background = 'rgba(0, 50, 70, 0.9)';
    }
  }

  private async handleRegistration(): Promise<void> {
    const username = this.usernameInput.value.trim();
    const email = this.emailInput.value.trim();
    const password = this.passwordInput.value;
    const confirmPassword = this.confirmPasswordInput.value;

    if (!username || !email || !password || !confirmPassword) {
      this.updateStatus(I18n.t('register.fill_all_fields')); return;
    }
    if (username.length < 3) {
      this.updateStatus(I18n.t('register.name_too_short')); return;
    }
    if (password.length < 8) {
      this.updateStatus(I18n.t('register.password_too_short')); return;
    }
    if (password !== confirmPassword) {
      this.updateStatus(I18n.t('register.passwords_mismatch')); return;
    }
    if (!this.tosCheckbox.checked) {
      this.updateStatus('You must agree to the Terms of Service to continue.');
      this.tosCheckbox.focus();
      return;
    }

    this.isRegistering = true;
    this.setButtonLoading(true);

    try {
      let response: Response;
      const payload: RegistrationPayload = { username, email, password, tosAccepted: true };
      const refCode = getReferralCode();
      if (refCode) payload.refCode = refCode;
      try {
        response = await fetch(`${API_URL}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch (_fetchError) {
        throw new Error(I18n.t('login.server_unavailable'));
      }

      let result: any;
      try {
        result = await response.json();
      } catch (_jsonError) {
        throw new Error(I18n.t('login.server_unavailable'));
      }

      if (!response.ok || !result.success) {
        throw new Error(result.error?.message || I18n.t('register.registration_failed'));
      }

      this.updateStatus(I18n.t('register.account_created_login'));

      // Auto-login with the same credentials so the user goes straight to the game
      try {
        const loginResponse = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, locale: localStorage.getItem('crimson_mandate_locale') || 'en' }),
        });
        const loginResult = await loginResponse.json();
        if (loginResponse.ok && loginResult.success && loginResult.data?.token) {
          localStorage.setItem('crimsonmandate_token', loginResult.data.token);
          localStorage.setItem('crimsonMandate_token', loginResult.data.token);

          // Connect directly to game via WebSocket (same pattern as MMOLoginScene.performLogin)
          const token = loginResult.data.token;
          const userId = loginResult.data.userId;
          const mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);
          const mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);

          mmoState.initialize(userId, 1);
          this.updateStatus(I18n.t('register.account_created_connect'));

          // 15s watchdog so a silently-dropped WebSocket on mobile (cellular hiccup,
          // iOS Safari tab suspend) can't leave the user stuck on "Connecting..." forever.
          let watchdog: Phaser.Time.TimerEvent | null = null;
          const cleanupListeners = () => {
            EventBus.off(MMOEvents.CONNECTED, onConnected);
            EventBus.off(MMOEvents.AUTHENTICATED, onAuthenticated);
            EventBus.off(MMOEvents.WORLD_STATE, onWorldJoined);
            EventBus.off(MMOEvents.ERROR, onError);
            EventBus.off(MMOEvents.DISCONNECTED, onDisconnected);
            EventBus.off(MMOEvents.RECONNECT_FAILED, onError);
            if (watchdog) { watchdog.remove(); watchdog = null; }
          };
          const onConnected = () => {
            console.log('[Register] Connected to WebSocket');
          };
          const onAuthenticated = (payload: any) => {
            console.log('[Register] Authenticated:', payload);
            mmoNetwork.sendJoinWorld(1);
          };
          const onWorldJoined = (payload: any) => {
            console.log('[Register] Successfully joined world');
            mmoState.setWorldState(payload);
            cleanupListeners();

            this.time.delayedCall(500, () => {
              this.cameras.main.fadeOut(300, 0, 0, 0);
              this.time.delayedCall(300, () => {
                this.cleanupInputs();
                const game = this.scene.manager.game;
                const hudSceneKey = getHUDSceneKey();
                game.scene.stop('MMORegisterScene');
                game.scene.start('MMOGameScene');
                game.scene.start(hudSceneKey);
              });
            });
          };
          const onDisconnected = () => {
            if (!this.isRegistering) return;
            console.warn('[Register] WebSocket dropped during signup handoff; waiting for reconnect');
            this.updateStatus(I18n.t('register.account_created_connect'));
          };
          const onError = (error: any) => {
            console.error('[Register] Connection error:', error);
            cleanupListeners();
            // Surface the failure to the user instead of silently freezing.
            this.updateStatus(I18n.t('register.connection_failed'));
            this.isRegistering = false;
            this.setButtonLoading(false);
          };

          EventBus.on(MMOEvents.CONNECTED, onConnected);
          EventBus.on(MMOEvents.AUTHENTICATED, onAuthenticated);
          EventBus.on(MMOEvents.WORLD_STATE, onWorldJoined);
          EventBus.on(MMOEvents.ERROR, onError);
          EventBus.on(MMOEvents.DISCONNECTED, onDisconnected);
          EventBus.on(MMOEvents.RECONNECT_FAILED, onError);

          // Hard timeout in case no event fires at all
          watchdog = this.time.delayedCall(CONNECTION_WATCHDOG_MS, () => {
            onError(new Error('Connection timed out'));
          });

          mmoNetwork.connect(token);
          return; // Don't fall through to login scene redirect
        }
      } catch {
        // Login failed — fall through to redirect to login scene
      }

      // Fallback: redirect to login scene if auto-login API call failed
      this.cleanupInputs();
      this.scene.start('MMOLoginScene');
    } catch (error) {
      console.error('[Register] Error:', error);
      this.updateStatus(error instanceof Error ? error.message : I18n.t('register.registration_failed'));
      this.isRegistering = false;
      this.setButtonLoading(false);
    }
  }

  private updateStatus(message: string): void {
    if (!this.statusElement) return;
    if (message) {
      this.statusElement.textContent = message;
      this.statusElement.style.display = 'block';
      if (message.toLowerCase().includes('error') || message.toLowerCase().includes('failed') || message.toLowerCase().includes('must') || message.toLowerCase().includes('match')) {
        this.statusElement.style.color = '#ff6666';
      } else if (message.includes('created') || message.includes('Redirecting')) {
        this.statusElement.style.color = '#00ff88';
      } else {
        this.statusElement.style.color = '#00ffff';
      }
    } else {
      this.statusElement.style.display = 'none';
    }
  }

  private cleanupInputs(): void {
    if (this.inputContainer?.parentNode) {
      this.inputContainer.parentNode.removeChild(this.inputContainer);
    }
    if (this.logoContainer?.parentNode) {
      this.logoContainer.parentNode.removeChild(this.logoContainer);
      this.logoContainer = null;
    }
  }

  shutdown(): void {
    this.events.off('update', this.updateParallax, this);
    this.cleanupInputs();
  }
}
